import { useEffect, useMemo, useState } from "react";
import api, { fmtIDR, fmtDate } from "@/lib_app/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import DateInput from "@/components/app/DateInput";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Line, ComposedChart, PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Target as TargetIcon, Trophy, Users as UsersIcon, Percent, PieChart as PieIcon, Star, Building2, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/app/Avatar";

const PALETTE = ["#090808", "#97ADD4", "#FFDB52", "#57D28A", "#FF6B7D", "#FFA766"];
const CAT_LABEL = { pemerintah: "Pemerintah", bumn: "BUMN", swasta: "Swasta", partner: "Partner" };
const CAT_TINT = {
  pemerintah: "bg-red-100 text-red-700",
  bumn: "bg-blue-100 text-blue-700",
  swasta: "bg-[#C9F0D6] text-[#4A6D53]",
  partner: "bg-[#DCE0E7] text-[#626061]",
};
const CATEGORY_TABS = [
  { key: "all", label: "Semua" },
  { key: "pemerintah", label: "Pemerintah" },
  { key: "bumn", label: "BUMN" },
  { key: "swasta", label: "Swasta" },
  { key: "partner", label: "Partner" },
];
const MONTH_LABEL = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
const monthShort = (ymd) => { const [y,m]=ymd.split("-"); return `${MONTH_LABEL[+m-1]} ${y.slice(2)}`; };
const monthLong = (ymd) => { const [y,m]=ymd.split("-"); return `${MONTH_LABEL[+m-1]} ${y}`; };

// Compact Indonesian formatter (uses comma as decimal separator, dot as thousand).
const fmtCompactIDR = (v) => {
  const n = Number(v || 0);
  if (n >= 1e12) return `${(n / 1e12).toFixed(0)} T`;
  if (n >= 1e9)  return `${(n / 1e9).toFixed(0)} M`;
  if (n >= 1e6)  return `${(n / 1e6).toFixed(0)} Jt`;
  if (n >= 1e3)  return `${(n / 1e3).toFixed(0)} rb`;
  return n.toLocaleString("id-ID");
};

export default function SalesReport() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [period, setPeriod] = useState("ytd");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [category, setCategory] = useState("all");
  const [salesId, setSalesId] = useState("all");
  const [salesList, setSalesList] = useState([]);
  const [title, setTitle] = useState("Laporan Penjualan Perusahaan");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Load fiscal years & users list
  useEffect(() => {
    api.get("/reports/years").then((r) => {
      const arr = r.data?.years || [];
      if (arr.length) {
        setYears(arr);
        // If current selected year is not in list, snap to newest
        if (!arr.includes(year)) setYear(arr[0]);
      }
    }).catch(() => {});
    api.get("/users").then((r) => setSalesList((r.data || []).filter((u) => u.is_active !== false && u.role !== "owner" && u.role !== "finance"))).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setBusy(true);
    try {
      const params = { period };
      if (period === "custom") { params.start = start; params.end = end; }
      if (period === "year") { params.year = year; }
      if (category !== "all") params.category = category;
      if (salesId !== "all") params.sales_id = salesId;
      const r = await api.get("/reports/sales", { params });
      setData(r.data);
    } catch (err) {
      toast.error("Gagal memuat report");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, year, category, salesId]);

  const exportPdf = async () => {
    if (!isOwner) return;
    setExporting(true);
    try {
      const body = { period, title };
      if (period === "custom") { body.start = start; body.end = end; }
      if (period === "year") { body.year = year; }
      if (category !== "all") body.category = category;
      if (salesId !== "all") body.sales_id = salesId;
      const r = await api.post("/reports/sales/pdf", body, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/);
      a.download = m ? decodeURIComponent(m[1]) : `Sales_Report_${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF ter-generate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal export");
    } finally { setExporting(false); }
  };

  const monthlyChart = useMemo(() => (data?.monthly || []).map((m) => ({
    month: monthShort(m.month),
    prospek: m.prospects_new,
    deal: m.deal_value,
    target: m.target,
  })), [data]);

  // Win / Loss / Ongoing pie (replacement for Funnel)
  const winLossData = useMemo(() => {
    if (!data?.funnel) return [];
    const f = data.funnel;
    const ongoing = (f.hot || 0) + (f.warm || 0) + (f.cold || 0);
    const out = [
      { name: "Deal (Won)", value: f.deal || 0, fill: "#57D28A" },
      { name: "Lost", value: f.lost || 0, fill: "#FF6B7D" },
      { name: "Ongoing", value: ongoing, fill: "#FFDB52" },
    ];
    return out;
  }, [data]);
  const winLossTotal = useMemo(() => winLossData.reduce((s, x) => s + x.value, 0), [winLossData]);

  const catData = useMemo(() => (data?.by_category || []).map((c) => ({
    name: CAT_LABEL[c.category] || c.category, value: c.deal_value,
  })), [data]);

  return (
    <div className="space-y-6" data-testid="sales-report-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Laporan Perusahaan</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">{title}</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">Ringkasan penjualan seluruh tim untuk periode terpilih.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Tim</Label>
            <Select value={salesId} onValueChange={setSalesId}>
              <SelectTrigger data-testid="sales-filter" className="h-11 rounded-full bg-white card-soft border-transparent px-5 w-52 mt-1">
                <SelectValue placeholder="Pilih anggota tim..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Tim</SelectItem>
                {salesList.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    <div className="flex items-center gap-2">
                      <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                      <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PeriodFilter
            period={period} setPeriod={setPeriod}
            start={start} setStart={setStart}
            end={end} setEnd={setEnd}
            year={year} setYear={setYear} years={years}
            onApply={load}
          />
        </div>
      </div>

      {/* Category filter row */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex flex-wrap items-center gap-2 bg-white card-soft rounded-full p-1 w-fit" data-testid="category-tabs">
          {CATEGORY_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setCategory(t.key)}
              data-testid={`category-tab-${t.key}`}
              className={`inline-flex items-center h-9 px-4 rounded-full text-xs font-semibold transition-all ${
                category === t.key
                  ? (t.key === "all" ? "bg-[#090808] text-white" : "bg-[#090808] text-white shadow-[0_4px_10px_rgba(9,8,8,0.20)]")
                  : "text-[#626061] hover:text-[#090808]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {!data && !busy && <div className="text-sm text-[#A0A3A8]">Memuat...</div>}

      {data && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Deal Value" value={fmtIDR(data.totals.deal_value)} icon={<Trophy className="w-4 h-4" />} accent="emerald" sub={<ComparisonBadge delta={data.comparison.delta_pct} />} testid="kpi-deal-value" />
            <KpiCard label="Target" value={fmtIDR(data.totals.target)} icon={<TargetIcon className="w-4 h-4" />} accent="violet" sub={<span className="text-[10px] text-[#A0A3A8]">akumulasi periode</span>} testid="kpi-target" />
            <KpiCard label="Achievement" value={`${data.totals.achievement_pct.toFixed(2)}%`} icon={<TargetIcon className="w-4 h-4" />} accent={data.totals.achievement_pct >= 100 ? "emerald" : data.totals.achievement_pct >= 70 ? "amber" : "rose"} sub={<span className="text-[10px] text-[#A0A3A8]">deal ÷ target</span>} />
            <KpiCard label="Win Rate" value={`${data.totals.win_rate.toFixed(2)}%`} icon={<Percent className="w-4 h-4" />} accent="violet" sub={<span className="text-[10px] text-[#A0A3A8]">{data.totals.deal_count} deal berhasil</span>} />
          </div>

          {/* Deal vs Target chart */}
          <Card className="p-5 border-0 rounded-2xl card-soft">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#090808]">Deal vs Target per Bulan</h3>
              <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Deal • Prospek Baru • Target</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={monthlyChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DCE0E7" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#A0A3A8" }} />
                <YAxis yAxisId="left" tickFormatter={fmtCompactIDR} tick={{ fontSize: 10, fill: "#A0A3A8" }} width={68} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => v.toLocaleString("id-ID")} tick={{ fontSize: 10, fill: "#A0A3A8" }} width={40} />
                <Tooltip
                  formatter={(v, n) => {
                    if (n === "Prospek Baru") return [Number(v).toLocaleString("id-ID"), n];
                    if (n === "Deal Value" || n === "Target") return [fmtIDR(v), n];
                    return [v, n];
                  }}
                  contentStyle={{ borderRadius: 12, border: "1px solid #DCE0E7", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="deal" name="Deal Value" fill="#090808" radius={[8, 8, 0, 0]} />
                <Line yAxisId="left" type="monotone" dataKey="target" name="Target" stroke="#FFDB52" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 4" />
                <Bar yAxisId="right" dataKey="prospek" name="Prospek Baru" fill="#97ADD4" radius={[8, 8, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          {/* Win/Loss + Category pie */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="p-5 border-0 rounded-2xl card-soft" data-testid="win-loss-card">
              <h3 className="text-sm font-bold text-[#090808] mb-3 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#57D28A]" /> Win / Loss / Ongoing</h3>
              {winLossTotal === 0 ? (
                <div className="text-sm text-[#A0A3A8] text-center py-16">Belum ada data prospek.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={winLossData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                        {winLossData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [`${Number(v).toLocaleString("id-ID")} prospek`, n]} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2">
                    {winLossData.map((s) => {
                      const pct = winLossTotal ? (s.value / winLossTotal) * 100 : 0;
                      return (
                        <div key={s.name} className="flex items-center gap-3">
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.fill }} />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-[#626061] font-semibold">{s.name}</div>
                            <div className="relative h-1.5 bg-[#F4F5F7] rounded-full mt-1 overflow-hidden">
                              <div className="absolute h-full rounded-full" style={{ width: `${pct}%`, background: s.fill }} />
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-bold text-[#090808] tabular-nums">{s.value.toLocaleString("id-ID")}</div>
                            <div className="text-[10px] text-[#A0A3A8] tabular-nums">{pct.toFixed(2)}%</div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="pt-2 mt-2 border-t border-[#DCE0E7] flex justify-between text-[10px] uppercase tracking-widest font-semibold">
                      <span className="text-[#A0A3A8]">Win Rate</span>
                      <span className="text-[#57D28A]">{data.totals.win_rate.toFixed(2)}%</span>
                    </div>
                  </div>
                </div>
              )}
            </Card>
            <Card className="p-5 border-0 rounded-2xl card-soft">
              <h3 className="text-sm font-bold text-[#090808] mb-3 flex items-center gap-1.5"><PieIcon className="w-4 h-4" /> Deal per Kategori Klien</h3>
              {catData.length === 0 ? (
                <div className="text-sm text-[#A0A3A8] text-center py-16">Belum ada data deal.</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={catData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {catData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtIDR(v)} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* Cash Flow (Proyek Payments) */}
          {data.cash_flow && (
            <Card
              data-testid="cashflow-report-card"
              className="p-5 border-0 rounded-2xl card-soft"
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-sm font-bold text-[#090808] flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-[#090808]" /> Cash Flow Proyek
                </h3>
                <div className="inline-flex gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">
                    {data.cash_flow.payment_count} transaksi · {data.cash_flow.active_projects} proyek aktif
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <div className="rounded-xl p-3 bg-[#F4F5F7]" data-testid="cashflow-total-paid">
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Total Terbayar</div>
                  <div className="text-xl font-bold text-[#3EB876] tabular-nums font-num mt-0.5">{fmtIDR(data.cash_flow.total_paid)}</div>
                </div>
                <div className="rounded-xl p-3 bg-[#F4F5F7]" data-testid="cashflow-outstanding">
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Outstanding</div>
                  <div className="text-xl font-bold text-[#CE6968] tabular-nums font-num mt-0.5">{fmtIDR(data.cash_flow.outstanding)}</div>
                </div>
                <div className="rounded-xl p-3 bg-[#F4F5F7]" data-testid="cashflow-overdue">
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Proyek Overdue</div>
                  <div className="text-xl font-bold text-[#3B4A63] tabular-nums font-num mt-0.5">{data.cash_flow.overdue_count}</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={(data.cash_flow.monthly || []).map((m) => ({ month: monthShort(m.month), paid: m.paid }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DCE0E7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#A0A3A8" }} />
                  <YAxis tickFormatter={fmtCompactIDR} tick={{ fontSize: 10, fill: "#A0A3A8" }} width={68} />
                  <Tooltip formatter={(v) => [fmtIDR(v), "Terbayar"]} contentStyle={{ borderRadius: 12, border: "1px solid #DCE0E7", fontSize: 12 }} />
                  <Bar dataKey="paid" name="Terbayar" fill="#3EB876" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Ranking Sales */}
          <Card className="p-5 border-0 rounded-2xl card-soft">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#090808] flex items-center gap-1.5"><UsersIcon className="w-4 h-4" /> Ranking Sales</h3>
              <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">berdasar Deal Value periode</span>
            </div>
            {data.by_sales.length === 0 ? (
              <div className="text-sm text-[#A0A3A8] text-center py-8">Belum ada data.</div>
            ) : (
              <div className="space-y-2">
                {data.by_sales.map((s, i) => {
                  const max = data.by_sales[0].deal_value || 1;
                  const pct = (s.deal_value / max) * 100;
                  return (
                    <div key={s.user_id} data-testid={`ranking-row-${s.user_id}`} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full font-bold text-xs flex items-center justify-center ${i === 0 ? "bg-[#FFE89A] text-[#B39548]" : i === 1 ? "bg-[#B9C5D8] text-[#626061]" : i === 2 ? "bg-[#FFD7B5] text-orange-600" : "bg-[#F4F5F7] text-[#A0A3A8]"}`}>{i + 1}</div>
                      <Avatar name={s.name} picture={s.picture} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#090808] truncate">{s.nickname}</div>
                        <div className="relative h-2 bg-[#F4F5F7] rounded-full mt-1 overflow-hidden">
                          <div className="absolute h-full bg-gradient-to-r from-[#090808] to-[#97ADD4] rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-[#090808] tabular-nums">{fmtIDR(s.deal_value)}</div>
                        <div className="text-[10px] text-[#A0A3A8]">{s.deal_count} deal</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Monthly breakdown table */}
          <Card className="border-0 rounded-2xl card-soft overflow-hidden" data-testid="monthly-breakdown">
            <div className="px-5 py-3 border-b border-[#DCE0E7]">
              <h3 className="text-sm font-bold text-[#090808]">Breakdown per Bulan</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F5F7] text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">
                  <tr>
                    <th className="text-left px-5 py-3">Bulan</th>
                    <th className="text-right px-3 py-3">Prospek Baru</th>
                    <th className="text-right px-3 py-3">Deal Count</th>
                    <th className="text-right px-3 py-3">Deal Value</th>
                    <th className="text-right px-3 py-3">Target</th>
                    <th className="text-right px-5 py-3">Achievement</th>
                  </tr>
                </thead>
                <tbody>
                  {data.monthly.map((m) => {
                    const ach = m.achievement_pct || 0;
                    const tint = ach >= 100 ? "text-[#57D28A]" : ach >= 70 ? "text-[#B39548]" : "text-[#FF6B7D]";
                    return (
                      <tr key={m.month} className="border-t border-[#DCE0E7]">
                        <td className="px-5 py-2.5 font-semibold text-[#090808]">{monthLong(m.month)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{m.prospects_new}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{m.deal_count}</td>
                        <td className="px-3 py-2.5 text-right font-num text-xs font-bold text-[#090808] tabular-nums">{fmtIDR(m.deal_value)}</td>
                        <td className="px-3 py-2.5 text-right font-num text-xs text-[#A0A3A8] tabular-nums">{fmtIDR(m.target)}</td>
                        <td className={`px-5 py-2.5 text-right font-bold tabular-nums ${tint}`}>{ach.toFixed(2)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top 10 Deals */}
            <Card className="p-5 border-0 rounded-2xl card-soft" data-testid="top-deals-card">
              <h3 className="text-sm font-bold text-[#090808] mb-3 flex items-center gap-1.5"><Star className="w-4 h-4 text-[#FFDB52]" /> Top 10 Deal (Periode Ini)</h3>
              {data.top_deals.length === 0 ? (
                <div className="text-sm text-[#A0A3A8] text-center py-8">Belum ada deal periode ini.</div>
              ) : (
                <div className="space-y-2">
                  {data.top_deals.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-[#DCE0E7] last:border-0">
                      <div className={`w-6 h-6 rounded-full font-bold text-[11px] flex items-center justify-center ${i === 0 ? "bg-[#FFE89A] text-[#B39548]" : i === 1 ? "bg-[#B9C5D8] text-[#626061]" : i === 2 ? "bg-[#FFD7B5] text-orange-600" : "bg-[#F4F5F7] text-[#A0A3A8]"}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-[#090808] truncate">{d.customer}</div>
                        <div className="text-[11px] text-[#A0A3A8] truncate">{d.service} · {d.owner_nickname} · {fmtDate(d.deal_date)}</div>
                      </div>
                      <div className="text-sm font-bold text-[#57D28A] tabular-nums shrink-0">{fmtIDR(d.value)}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Klien Baru */}
            <Card className="p-5 border-0 rounded-2xl card-soft" data-testid="new-clients-card">
              <h3 className="text-sm font-bold text-[#090808] mb-3 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-[#090808]" /> Klien Baru ({data.new_clients.count})</h3>
              {data.new_clients.count === 0 ? (
                <div className="text-sm text-[#A0A3A8] text-center py-8">Belum ada client baru.</div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {data.new_clients.list.slice(0, 15).map((c) => (
                    <div key={c.id} className="flex items-center gap-3 py-1.5 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#090808] truncate">{c.name}</div>
                        <div className="text-[10px] text-[#A0A3A8] tabular-nums">{c.first_prospect_date?.slice(0, 10)} · oleh {c.owner_nickname}</div>
                      </div>
                      <span className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold ${CAT_TINT[c.category] || "bg-[#DCE0E7] text-[#626061]"}`}>{CAT_LABEL[c.category] || c.category}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Export PDF button (owner only) */}
          {isOwner && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={exportPdf}
                disabled={exporting || !data}
                data-testid="export-sales-pdf-btn"
                className="gap-2 h-11 rounded-full bg-[#090808] hover:bg-[#626061] px-6 font-semibold"
              >
                <Download className="w-4 h-4" /> {exporting ? "Membuat PDF..." : "Export Report PDF"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon, accent = "violet", sub, testid }) {
  const tint = {
    violet: "bg-[#DCE0E7] text-[#090808]",
    emerald: "bg-[#C9F0D6] text-[#57D28A]",
    amber: "bg-[#FFE89A] text-[#B39548]",
    rose: "bg-[#FFD7B5] text-[#FF6B7D]",
    slate: "bg-[#DCE0E7] text-[#626061]",
  }[accent];
  return (
    <Card className="p-4 border-0 rounded-2xl card-soft" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">{label}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tint}`}>{icon}</div>
      </div>
      <div className="text-xl font-bold text-[#090808]">{value}</div>
      {sub && <div className="mt-1">{sub}</div>}
    </Card>
  );
}

function ComparisonBadge({ delta }) {
  const pos = delta >= 0;
  const Icon = pos ? TrendingUp : TrendingDown;
  const tint = pos ? "text-[#57D28A] bg-emerald-50" : "text-[#FF6B7D] bg-[#F8ECE5]";
  return (
    <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-bold ${tint}`} data-testid="kpi-comparison">
      <Icon className="w-3 h-3" /> {pos ? "+" : ""}{delta.toFixed(2)}% vs periode lalu
    </span>
  );
}

export function PeriodFilter({ period, setPeriod, start, setStart, end, setEnd, year, setYear, years = [], onApply }) {
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Periode</Label>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger data-testid="report-period-filter" className="h-11 rounded-full bg-white card-soft border-transparent px-5 w-44 mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Bulan ini</SelectItem>
            <SelectItem value="ytd">YTD (tahun ini)</SelectItem>
            <SelectItem value="year">Tahun Fiskal</SelectItem>
            <SelectItem value="custom">Custom range</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {period === "year" && setYear && (
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Tahun</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="report-year-filter" className="h-11 rounded-full bg-white card-soft border-transparent px-5 w-32 mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(years.length ? years : [new Date().getFullYear()]).map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {period === "custom" && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Dari</Label>
            <DateInput value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-custom-start" className="h-11 rounded-full bg-white card-soft border-transparent px-4 w-36" wrapperClassName="mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Sampai</Label>
            <DateInput value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-custom-end" className="h-11 rounded-full bg-white card-soft border-transparent px-4 w-36" wrapperClassName="mt-1" />
          </div>
          <Button data-testid="report-apply-btn" onClick={onApply} className="h-11 rounded-full bg-[#090808] hover:bg-[#000000] px-6">Terapkan</Button>
        </>
      )}
    </div>
  );
}
